import { useRef, useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { domToBlob, domToDataUrl } from "modern-screenshot";
import Button from "../../Button";
import ConfirmationModal from "../../../../../newUXComponents/Modal/ConfirmationModal";
import SquareShape from "../../Shapes/Squares";
import VerticalRectangleShape from "../../Shapes/Vertical Rectangles";
import HorizontalRectangleShape from "../../Shapes/Horizontal Rectangles";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { setMediaKit } from "../../../../../redux/modules/AI_Creator/actions";
import api from "../../../../../service";
import generateHeaders from "../../../../../utils/generateHeaders";
import useNotification from "../../../../../hooks/newUXNotification";

export default function ComponentToPngConverter({
  logoSrc,
  texts,
  confirmedImage,
  layout,
  positions,
  positionsHR,
  positionsVR,
  squareModalScale,
  verticalRectangleScale,
  horizontalRectangleScale,
  convertPositionRatio,
  x_axis,
}) {
  const intl = useIntl();
  const squareRef = useRef(null);
  const verticalRef = useRef(null);
  const horizontalRef = useRef(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const { token } = useSelector((state) => state.auth);
  const { event, contract } = useSelector((state) => state.initialSettings);
  const notification = useNotification();
  const { unlimitedImages } = useSelector((state) => state.ai_creator);

  useEffect(() => {
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        setFontsLoaded(true);
      });
    } else {
      setTimeout(() => {
        setFontsLoaded(true);
      }, 1000);
    }
  }, []);

  const inlineStyles = (node) => {
    // Create a separate font face rules for the cloned element
    const poppinsFontFaces = `
      @font-face {
        font-family: 'PoppinsClone';
        src: url('https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLGT9Z1xlFQ.woff2') format('woff2');
        font-weight: 500;
        font-style: normal;
      }
      @font-face {
        font-family: 'PoppinsClone';
        src: url('https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLDD4Z1xlFQ.woff2') format('woff2');
        font-weight: 800;
        font-style: normal;
      }
    `;

    const styleElement = document.createElement("style");
    styleElement.textContent = poppinsFontFaces;
    node.prepend(styleElement);

    // Apply the cloned font to text elements
    const textElements = node.querySelectorAll(
      "p, span, h1, h2, h3, h4, h5, h6, div",
    );
    textElements.forEach((el) => {
      el.style.fontFamily = "'PoppinsClone', sans-serif";
    });

    // Apply other important styles
    const allElements = node.querySelectorAll("*");
    allElements.forEach((el) => {
      const computedStyle = window.getComputedStyle(el);
      const importantStyles = [
        "font-size",
        "font-weight",
        "font-style",
        "color",
        "line-height",
        "letter-spacing",
        "text-align",
        "text-transform",
        "text-decoration",
      ];

      importantStyles.forEach((prop) => {
        el.style[prop] = computedStyle.getPropertyValue(prop);
      });
    });
  };

  const captureComponent = async (ref, size) => {
    if (!ref.current) return null;

    try {
      const tempContainer = document.createElement("div");
      tempContainer.style.position = "absolute";
      tempContainer.style.left = "-9999px";
      tempContainer.style.width = `${size.width}px`;
      tempContainer.style.height = `${size.height}px`;
      tempContainer.style.pointerEvents = "none";
      document.body.appendChild(tempContainer);

      const clone = ref.current.cloneNode(true);
      tempContainer.appendChild(clone);
      inlineStyles(clone);

      const images = clone.querySelectorAll("img");
      await Promise.all(
        Array.from(images).map(async (img) => {
          if (img.src.startsWith("data:")) return;

          try {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            const newImg = new Image();

            await new Promise((resolve, reject) => {
              newImg.onload = resolve;
              newImg.onerror = reject;

              newImg.crossOrigin = "anonymous";
              newImg.src = img.src;
            });

            // Set canvas dimensions
            canvas.width = newImg.width || img.width || 200;
            canvas.height = newImg.height || img.height || 200;

            // Draw the image on the canvas
            ctx.drawImage(newImg, 0, 0, canvas.width, canvas.height);

            // Replace the src with the data URL
            img.src = canvas.toDataURL("image/png");
          } catch (error) {
            console.warn(
              `Could not convert image ${img.src} to data URL:`,
              error,
            );
          }
        }),
      );

      const dataUrl = await domToDataUrl(clone, {
        cacheBust: true,
        quality: 1.0,
        fontEmbedCSS: true,
        skipFonts: false,
      });

      // ! Get blob for backend submission
      const blob = await domToBlob(clone, {
        cacheBust: true,
        quality: 1.0,
        fontEmbedCSS: true,
        skipFonts: false,
      });

      document.body.removeChild(tempContainer);

      return { dataUrl, blob };
    } catch (error) {
      console.error("Error capturing image:", error);
      return null;
    }
  };

  const handleCapture = async () => {
    if (!fontsLoaded) {
      console.warn(
        "Fonts may not be fully loaded yet. This might affect text rendering.",
      );
    }

    setIsCapturing(true);

    try {
      if (document.fonts && typeof document.fonts.check === "function") {
        const isPoppinsLoaded = await document.fonts.check('12px "Poppins"');
        if (!isPoppinsLoaded) {
          console.warn(
            "Poppins font is not loaded. Attempting to load it now...",
          );

          const weights = [500, 800];
          const fontLoaders = [];

          for (const weight of weights) {
            const fontLoader = new FontFace(
              "Poppins",
              `url(https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLGT9Z1xlFQ.woff2)`,
              {
                style: "normal",
                weight: weight.toString(),
              },
            );
            fontLoaders.push(fontLoader);
          }

          try {
            const loadedFonts = await Promise.all(
              fontLoaders.map((font) => font.load()),
            );
            loadedFonts.forEach((font) => document.fonts.add(font));
          } catch (fontError) {
            console.error("Failed to force-load Poppins fonts:", fontError);
          }
        }
      }

      const results = await Promise.all([
        captureComponent(squareRef, { width: 1080, height: 1350 }),
        captureComponent(verticalRef, { width: 1080, height: 1920 }),
        captureComponent(horizontalRef, { width: 1200, height: 628 }),
      ]);

      const validResults = results.filter((result) => result !== null);

      if (validResults.length > 0) {
        const dataUrls = validResults.map((r) => r.dataUrl);

        try {
          const options = generateHeaders(token);
          const formData = createFormData({
            eventId: event.value,
            opportunityId: contract.value,
            imageSquare: base64ToFile({
              base64String: dataUrls[0],
              fileName: "square",
            }),
            imageVertical: base64ToFile({
              base64String: dataUrls[1],
              fileName: "vertical",
            }),
            imageHorizontal: base64ToFile({
              base64String: dataUrls[2],
              fileName: "horizontal",
            }),
            logo: base64ToFile({ base64String: logoSrc, fileName: "logo" }),
          });
          await api.post("/media-kit", formData, options);
          dispatch(
            setMediaKit({
              dataUrls,
            }),
          );
          history.push("/ai-creator/media-kit");
        } catch (error) {
          notification.displayError(
            intl.formatMessage({
              id: "ai_creator_posting_error",
            }),
            "ai_creator_posting_error",
          );
        }
      }

      // const shapeTypes = ["square", "vertical", "horizontal"];
      // validResults.forEach((result, index) => {
      //   if (!result) return;

      //   const link = document.createElement("a");
      //   link.download = `${fileName}-${shapeTypes[index]}.png`;
      //   link.href = result.dataUrl;
      //   link.click();
      // });
    } catch (error) {
      console.error("Error in capture process:", error);
    } finally {
      setIsCapturing(false);
    }
  };

  const handleOpenConfirm = () => {
    setIsConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    setIsConfirmOpen(false);
  };

  return (
    <div style={{ width: "100%" }}>
      {/* Hidden components to capture */}
      <div
        style={{ position: "absolute", left: "-9999px", visibility: "hidden" }}
      >
        {/* Square Shape */}
        <div
          ref={squareRef}
          style={{
            width: "1080px",
            height: "1350px",
            overflow: "hidden",
          }}
        >
          <SquareShape
            logoSrc={logoSrc}
            callText={texts.callText}
            imageUrl={confirmedImage}
            primaryText={texts.primaryText}
            secondaryText={texts.secondaryText}
            shape={layout.layout}
            variantColor={layout.color}
            x_axis={x_axis.square}
            size={1080}
            positions={{
              imagePosition: convertPositionRatio({
                currentSize: 1080,
                draggableSize: squareModalScale,
                positions: positions[0],
              }),
              logoPosition: convertPositionRatio({
                currentSize: 1080,
                draggableSize: squareModalScale,
                positions: positions[1],
              }),
              labelsPosition: convertPositionRatio({
                currentSize: 1080,
                draggableSize: squareModalScale,
                positions: positions[2],
              }),
            }}
          />
        </div>

        {/* Vertical Rectangle Shape */}
        <div
          ref={verticalRef}
          style={{
            width: "1080px",
            height: "1920px",
            overflow: "hidden",
          }}
        >
          <VerticalRectangleShape
            logoSrc={logoSrc}
            callText={texts.callText}
            imageUrl={confirmedImage}
            primaryText={texts.primaryText}
            secondaryText={texts.secondaryText}
            shape={layout.layout}
            variantColor={layout.color}
            x_axis={x_axis.vr}
            size={1080}
            positions={{
              imagePosition: convertPositionRatio({
                currentSize: 1080,
                draggableSize: verticalRectangleScale,
                positions: positionsVR[0],
              }),
              logoPosition: convertPositionRatio({
                currentSize: 1080,
                draggableSize: verticalRectangleScale,
                positions: positionsVR[1],
              }),
              labelsPosition: convertPositionRatio({
                currentSize: 1080,
                draggableSize: verticalRectangleScale,
                positions: positionsVR[2],
              }),
            }}
          />
        </div>

        {/* Horizontal Rectangle Shape */}
        <div
          ref={horizontalRef}
          style={{
            width: "1200px",
            height: "628px",
            overflow: "hidden",
          }}
        >
          <HorizontalRectangleShape
            logoSrc={logoSrc}
            callText={texts.callText}
            imageUrl={confirmedImage}
            primaryText={texts.primaryText}
            secondaryText={texts.secondaryText}
            shape={layout.layout}
            variantColor={layout.color}
            x_axis={x_axis.hr}
            size={1200}
            positions={{
              imagePosition: convertPositionRatio({
                currentSize: 1200,
                draggableSize: horizontalRectangleScale,
                positions: positionsHR[0],
              }),
              logoPosition: convertPositionRatio({
                currentSize: 1200,
                draggableSize: horizontalRectangleScale,
                positions: positionsHR[1],
              }),
              labelsPosition: convertPositionRatio({
                currentSize: 1200,
                draggableSize: horizontalRectangleScale,
                positions: positionsHR[2],
              }),
            }}
          />
        </div>
      </div>

      <Button
        size={"modal"}
        loading={isCapturing}
        disabled={isCapturing}
        label={intl.formatMessage({ id: "ai_creator.preview.finish" })}
        onClick={handleOpenConfirm}
      />
      <ConfirmationModal
        id="modal-ai-creator-finish"
        open={isConfirmOpen}
        handleClose={handleCloseConfirm}
        title={intl.formatMessage({ id: "ai_creator.finish.creationMediaKit" })}
        message={
          unlimitedImages
            ? intl.formatMessage({
                id: "ai_creator.finish.creationMediaKit.confirm",
              })
            : intl.formatMessage({ id: "ai_creator.preview.finish.confirm" })
        }
        confirmation={handleCapture}
        labelBtnConfir="yes"
        labelClose="no"
        disabledBtnConfirm={isCapturing}
      />
    </div>
  );
}

function base64ToFile({ base64String, fileName }) {
  const mimeType = "image/png";

  const base64WithoutPrefix = base64String.includes(",")
    ? base64String.split(",")[1]
    : base64String;

  const byteCharacters = atob(base64WithoutPrefix);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: mimeType });
  return new File([blob], fileName, { type: mimeType });
}

function createFormData(data) {
  const formData = new FormData();

  formData.append("eventId", data.eventId);
  formData.append("opportunityId", data.opportunityId);
  formData.append("imageSquare", data.imageSquare);
  formData.append("imageVertical", data.imageVertical);
  formData.append("imageHorizontal", data.imageHorizontal);
  formData.append("logo", data.logo);

  return formData;
}
